import React, {MutableRefObject, useEffect, useRef, useState} from 'react'
import {
  Box,
  Text,
  Modal,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  IconButton,
  Image,
  ModalFooter,
  Flex,
  Spinner,
  Tooltip,
} from '@chakra-ui/react'
import TransIcon from '@assets/svgs/trans.svg'
import GlobalMic from '@components/GlobalMic/GlobalMic'
import VolumeIcon from '@assets/svgs/volume.svg'
import appRequest from '@apis/app'
import {ConfigProvider, Progress} from 'antd'
import learnRequest from '@apis/learn'
import CheckedWords from '@components/GlobalTrans/CheckedWords'
import {getColorScore} from '@utils'
import {
  IData,
  IDataSpeech,
  IGlobalTrans,
} from '@components/GlobalTrans/GlobalTransModel'
import BookMark from '@assets/svgs/learn/book_mark.svg'
import BookMarkSolid from '@assets/svgs/learn/bookmark_solid.svg'
import {useDispatch, useSelector} from 'react-redux'
import {
  updateIsOpenModalSaveKnowledge,
  updateTypeSaveFolder,
} from '@redux/notebook/notebookSlice'
import {updateIsDisableHandleText} from '@redux/conversation/converstationSlice'

const GlobalTrans = ({
  text,
  textTrans,
  savedPhrase,
  setSavedPhrase,
}: IGlobalTrans) => {
  const stateConversion = useSelector((state: any) => state.converstation)
  const {typeSaveFolder, isOpenModalSaveKnowledge} = useSelector(
    (state: any) => state.notebook,
  )
  const [data, setData] = useState<IData | undefined>()
  const [dataSpeech, setDataSpeech] = useState<IDataSpeech>()
  const [isLoading, setIsLoading] = useState<Boolean>(true)
  const [selectedText, setSelectedText] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [positionTrans, setPositionTrans] = useState<any>()
  const [showTranslateIcon, setShowTranslateIcon] = useState<Boolean>(false)
  const translateIconRef: MutableRefObject<any> = useRef(null)
  const audioRef: MutableRefObject<any> = useRef(null)
  const dispatch = useDispatch()

  const fetchWordInfo = async () => {
    await appRequest.getWordInfo({text: selectedText}).then(res => {
      if (res?.data) {
        setData(res.data?.data)
        setIsLoading(false)
      }
    })
  }

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        translateIconRef.current &&
        !translateIconRef.current.contains(event.target)
      ) {
        setShowTranslateIcon(false) // Hide translate icon when clicking outside of it
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (textTrans?.selectedText?.length > 0 && !showModal) {
      setSelectedText(textTrans.selectedText)
      setShowTranslateIcon(true)
      setPositionTrans({
        x: textTrans.left - 10,
        y: textTrans.top - 40,
      })
    }
  }, [textTrans?.selectedText])

  // Xử lý chặn handleTextSelect khi onMouseUp
  useEffect(() => {
    if (showModal || showTranslateIcon) {
      dispatch(updateIsDisableHandleText(true))
    } else {
      dispatch(updateIsDisableHandleText(false))
    }
  }, [showModal, showTranslateIcon])

  const handleTextSelect = (e: any) => {
    const selection: any = window.getSelection()
    const selectedText = selection.toString().trim()
    if (selectedText) {
      setSelectedText(selectedText)
      setShowTranslateIcon(true)
      setPositionTrans({
        x: e.clientX,
        y: e.clientY,
      })
    }
  }

  const handleTranslateIconClick = async () => {
    setShowModal(true)
    setShowTranslateIcon(false)
    await fetchWordInfo()
  }

  const handlePlayAudio = () => {
    audioRef.current = new Audio(data?.audio)
    audioRef.current.play().finally()
  }

  const handleSendAudio = async (blob: any) => {
    let file = new File([blob], 'audio.wav', {type: 'audio/wav'})
    const formData = new FormData()
    formData.append('audio-file', file)
    formData.append('text-refs', selectedText)
    await learnRequest.getSpeechCheck(formData).then(res => {
      if (res?.data) {
        setDataSpeech(res.data?.data)
      }
    })
  }
  const onCloseModal = () => {
    setSavedPhrase(false)
    setShowModal(false)
    setSelectedText('')
    setData(undefined)
    setDataSpeech(undefined)
    setIsLoading(true)
    audioRef.current?.pause()
  }

  const handleSave = () => {
    dispatch(updateIsOpenModalSaveKnowledge(true))
    dispatch(updateTypeSaveFolder('PHRASE'))
  }

  return (
    <Box>
      {text && (
        <Text
          onMouseUp={handleTextSelect}
          cursor="pointer"
          data-tip="Translate">
          {text}
        </Text>
      )}
      {showTranslateIcon && (
        <Box
          zIndex={99}
          bgColor={'transparent'}
          ref={translateIconRef}
          style={{
            position: 'absolute',
            left: positionTrans.x,
            top: positionTrans.y,
          }}>
          <IconButton
            onClick={handleTranslateIconClick}
            style={{cursor: 'pointer', backgroundColor: 'transparent'}}
            icon={<Image src={TransIcon} boxSize={'40px'} />}
            aria-label={'Search database'}
          />
        </Box>
      )}
      <Modal
        isCentered
        closeOnEsc
        isOpen={showModal}
        motionPreset="slideInBottom"
        onClose={onCloseModal}
        blockScrollOnMount={!isOpenModalSaveKnowledge}
        trapFocus={!isOpenModalSaveKnowledge}>
        <ModalContent
          m={0}
          py={'14px'}
          boxShadow="dark-lg"
          rounded="md"
          bg="white"
          borderRadius={'24px'}>
          {isLoading && (
            <Flex minH={400} minW={200} justifyContent={'center'}>
              <Spinner alignSelf={'center'} size="xl" />
            </Flex>
          )}
          {data && !isLoading && (
            <>
              <ModalBody flexDirection={'column'}>
                <Flex flexDirection={'row'} pb={'16px'}>
                  <IconButton
                    onClick={handlePlayAudio}
                    pr={'24px'}
                    style={{cursor: 'pointer', backgroundColor: 'transparent'}}
                    icon={<Image src={VolumeIcon} />}
                    justifyContent={'flex-start'}
                    alignItems={'flex-start'}
                    aria-label={'Search database'}
                  />
                  <Flex flex={1} flexDirection={'column'} flexWrap={'wrap'}>
                    {dataSpeech ? (
                      <CheckedWords data={dataSpeech?.result} />
                    ) : (
                      <Text
                        paddingInline={0}
                        textDecoration={'underline'}
                        fontWeight={'600'}
                        fontSize={'24px'}>
                        {data.text}
                      </Text>
                    )}
                    <Text>{data.ipa}</Text>
                  </Flex>
                  <ModalCloseButton />
                </Flex>
                <Text
                  minH={200}
                  borderRadius={'16px'}
                  bgColor={'#FAFAFA'}
                  variant="filled"
                  p={'8px'}
                  fontSize={'16px'}
                  fontWeight={'600'}
                  color={'rgba(0, 0, 0, 0.45)'}>
                  {data.text_vi}
                </Text>
              </ModalBody>
              {dataSpeech && (
                <Flex justifyContent={'center'}>
                  <ConfigProvider
                    theme={{
                      components: {
                        Progress: {
                          circleTextColor: getColorScore(
                            dataSpeech?.total_score * 100,
                          ),
                          circleTextFontSize: '38px',
                        },
                      },
                    }}>
                    <Progress
                      style={{fontWeight: '700'}}
                      // circleTextColor={'red'}
                      strokeColor={getColorScore(dataSpeech?.total_score * 100)}
                      type="dashboard"
                      percent={Math.floor(dataSpeech?.total_score * 100)}
                      format={percent => percent}
                    />
                  </ConfigProvider>
                </Flex>
              )}
              <ModalFooter
                flexDirection={'column'}
                justifyContent={'center'}
                p={0}
                position={'relative'}>
                <Text color={'rgba(0, 0, 0, 0.45)'} pb={'16px'}>
                  Check phát âm của bạn
                </Text>
                <GlobalMic onRecordingComplete={handleSendAudio} />
                <Tooltip
                  label={!savedPhrase ? 'Lưu vào sổ tay' : 'Đã lưu'}
                  placement={'top'}>
                  <Flex
                    h={'48px'}
                    w={'48px'}
                    position={'absolute'}
                    bottom={'10px'}
                    right={'60px'}
                    border={'2px solid #BAE7FF'}
                    bg={!savedPhrase ? 'white' : '#1890FF'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    borderRadius={'50%'}
                    cursor={'pointer'}
                    onClick={handleSave}>
                    {!savedPhrase ? (
                      <Image h={'32px'} w={'32px'} src={BookMarkSolid} />
                    ) : (
                      <Image h={'32px'} w={'32px'} src={BookMark} />
                    )}
                  </Flex>
                </Tooltip>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default GlobalTrans
